import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FeatureFlagService} from '@brunata-angular/util';
import {ExportFormat} from '../../shared/models/output-format.model';
import {
    ExportFileFormat,
    ExportFileFormatForm,
    FileFormatCapabilities,
} from '../../shared/models/export-file-format.model';
import {OptionSelectComponent} from '../../shared/ui/option-select/option-select.component';
import {StepCardDirective} from '../step-card/step-card.directive';
import {stepLabels} from '../step-card/step-labels.model';
import {FileFormatSettingsComponent} from './file-format-settings.component';

@Component({
    selector: 'app-file-format-step',
    standalone: true,
    providers: [
        stepLabels('quickexports_export_step_format_title', 'quickexports_export_step_format_hint'),
    ],
    hostDirectives: [{directive: StepCardDirective, inputs: ['titleKey', 'hintKey']}],
    imports: [FileFormatSettingsComponent, OptionSelectComponent],
    template: `
        <div class="format-panel">
            <app-option-select
                [options]="formats()"
                [selectedId]="selectedId()"
                name="export-format"
                ariaKey="quickexports_export_step_format_title"
                (optionSelected)="formatSelected.emit($event)" />

            @if (settingsEnabled()) {
                <app-file-format-settings
                    [form]="fileFormat()"
                    [settings]="settings()"
                    [capabilities]="capabilities()"
                    (settingsReset)="settingsReset.emit()" />
            }
        </div>
    `,
    styles: `
        .format-panel {
            --bs-border-color: rgba(var(--bs-primary-rgb), 0.15);
            --option-list-border: 0;
            border: 1px solid rgba(var(--bs-primary-rgb), 0.3);
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileFormatStepComponent {
    private readonly featureFlagService = inject(FeatureFlagService);

    protected readonly settingsEnabled = toSignal(
        this.featureFlagService.isFeatureFlagEnabled$('agi-19369-online-export-file-format'),
        {initialValue: false},
    );

    readonly formats = input.required<ReadonlyArray<ExportFormat>>();
    readonly selectedId = input.required<string>();
    readonly fileFormat = input.required<ExportFileFormatForm>();
    readonly settings = input.required<ExportFileFormat>();
    readonly carriesNumbers = input(true);
    readonly carriesDates = input(true);
    readonly carriesTimestamps = input(true);

    readonly formatSelected = output<string>();
    readonly settingsReset = output<void>();

    private readonly selectedOutputFormat = computed(
        () => this.formats().find(format => format.id === this.selectedId())?.outputFormat ?? null,
    );

    protected readonly capabilities = computed<FileFormatCapabilities>(() => ({
        characters: this.selectedOutputFormat() === 'CSV',
        numbers: this.carriesNumbers(),
        dates: this.carriesDates(),
        timestamps: this.carriesTimestamps(),
    }));
}
